import { Component, inject, Injector, Input, OnInit, signal } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import {
  CreateMaintenanceWorkOrderDto,
  MaintenanceWorkOrder,
  MaintenanceWorkOrderReason,
  MaintenanceWorkOrderType,
} from '../../../models/maintenance-work-order';
import { Asset, AssetCategory } from '../../../models/asset';
import { AssetSearchCommand } from '../../../models/commands/asset-search-command';
import { AssetProxyService } from '../../../proxies/asset.proxy.service';
import { CategoryProxyService } from '../../../proxies/category.proxy.service';
import { MaintenanceWorkOrderProxyService } from '../../../proxies/maintenance-work-order.proxy.service';
import { MaintenanceWorkOrderTypeProxyService } from '../../../proxies/maintenance-work-order-type.proxy.service';
import { MaintenanceWorkOrderReasonProxyService } from '../../../proxies/maintenance-work-order-reason.proxy.service';
import { GlobalMessageUtil } from '../../../utils/global-message-util';

@Component({
  selector: 'app-maintenance-reports-form-modal',
  templateUrl: './maintenance-reports-form-modal.component.html',
  styleUrl: './maintenance-reports-form-modal.component.scss',
})
export class MaintenanceReportsFormModalComponent extends BaseModalComponent<MaintenanceWorkOrder | null> implements OnInit {
  readonly maintenanceWorkOrderProxyService = inject(MaintenanceWorkOrderProxyService);
  readonly maintenanceWorkOrderTypeProxyService = inject(MaintenanceWorkOrderTypeProxyService);
  readonly maintenanceWorkOrderReasonProxyService = inject(MaintenanceWorkOrderReasonProxyService);
  readonly categoryProxyService = inject(CategoryProxyService);
  readonly assetProxyService = inject(AssetProxyService);

  @Input() report: MaintenanceWorkOrder | null = null;

  isLoading = signal(0);
  firstLoad = signal(true);
  maintenanceReports = signal<MaintenanceWorkOrder[]>([]);

  formGroup: FormGroup;
  assetCategory: FormControl<AssetCategory | null> = new FormControl(null);

  categories = signal<AssetCategory[] | null>([]);
  filteredCategories = signal<AssetCategory[]>([]);
  assets = signal<Asset[] | null>([]);
  filteredAssets = signal<Asset[]>([]);
  types = signal<MaintenanceWorkOrderType[] | null>([]);
  filteredTypes = signal<MaintenanceWorkOrderType[]>([]);
  reasons = signal<MaintenanceWorkOrderReason[] | null>([]);
  filteredReasons = signal<MaintenanceWorkOrderReason[]>([]);

  constructor() {
    const injector = inject(Injector);

    super(injector);
    this.formGroup = this._initializeForm();
  }

  private _initializeForm(): FormGroup {
    return new FormGroup({
      assetId: new FormControl<string | null>(null, [Validators.required]),
      siteCode: new FormControl<string | null>('NT'),
      maintenanceWorkOrderTypeReference: new FormControl<string | null>(null, [Validators.required]),
      maintenanceWorkOrderReasonReference: new FormControl<string | null>(null, [Validators.required]),
      isMachineDown: new FormControl<boolean>(false, { nonNullable: true }),
      submitterRemarks: new FormControl<string | null>(null),
      status: new FormControl<number | null>(0),
    });
  }

  ngOnInit(): void {
    this._initializeData();

    this.assetCategory.valueChanges.subscribe(() => {
      this.formGroup.get('assetId')?.setValue(null);
      this.filteredAssets.set([]);
    });
  }

  getFormData() {
    this.startLoading();
    if (this.report?.id) {
      this.maintenanceWorkOrderProxyService
        .getById(this.report?.id)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (response) => {
            if (response.data) {
              const assetCategoryObj = (this.categories() ?? []).find((t) => t.id === response.data!.assetCategoryReference) ?? null;

              const typeObj = (this.types() ?? []).find((t) => t.id === response.data!.maintenanceWorkOrderTypeReference) ?? null;
              const reasonObj = (this.reasons() ?? []).find((r) => r.id === response.data!.maintenanceWorkOrderReasonReference) ?? null;

              this.assetCategory.setValue(assetCategoryObj);

              this.formGroup.patchValue({
                assetId: response.data!.assetId ?? null,
                siteCode: response.data!.siteCode ?? 'NT',
                maintenanceWorkOrderTypeReference: typeObj,
                maintenanceWorkOrderReasonReference: reasonObj,
                isMachineDown: response.data!.isMachineDown ?? false,
                submitterRemarks: response.data!.submitterRemarks ?? null,
                status: this.report?.status ?? 0,
              });
            }
          },
          complete: () => {
            this.firstLoad.set(false);
          },
        });
    }
  }

  private _initializeData(): void {
    this.startLoading();
    forkJoin({
      categories: this.categoryProxyService.getCategories(),
      types: this.maintenanceWorkOrderTypeProxyService.getAll(),
      reasons: this.maintenanceWorkOrderReasonProxyService.getAll(),
    })
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe((response) => {
        this.categories.set(response.categories.data);
        this.types.set(response.types.data);
        this.reasons.set(response.reasons.data);

        if (this.report != null && this.firstLoad()) {
          this.getFormData();
        }
      });
  }

  categoryTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    const categories = this.categories() ?? [];
    this.filteredCategories.set(
      categories
        .filter((t) => t.name.toLowerCase().includes(query))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 8),
    );
  }

  assetTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query?.toLowerCase() ?? '';
    const categoryId = this.assetCategory.value?.id ?? '';

    if (!categoryId) {
      this.filteredAssets.set([]);
      return;
    }

    const request: AssetSearchCommand = {
      assetCategoryReference: categoryId,
    };

    this.startLoading();

    this.assetProxyService
      .searchAssets(request)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const responseData = response?.data as unknown;
          const assets = Array.isArray(responseData)
            ? responseData
            : Array.isArray((responseData as { items?: Asset[] })?.items)
              ? ((responseData as { items?: Asset[] }).items ?? [])
              : Array.isArray((responseData as { records?: Asset[] })?.records)
                ? ((responseData as { records?: Asset[] }).records ?? [])
                : [];

          this.assets.set(assets);
          this.filteredAssets.set(
            assets
              .filter((asset) => (asset.assetId ?? '').toLowerCase().includes(query))
              .sort((a, b) => (a.assetId ?? '').localeCompare(b.assetId ?? ''))
              .slice(0, 8),
          );
        },
      });
  }

  typeTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    const types = this.types() ?? [];
    this.filteredTypes.set(
      types
        .filter((t) => t.name.toLowerCase().includes(query))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 8),
    );
  }

  reasonTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    const reasons = this.reasons() ?? [];
    this.filteredReasons.set(
      reasons
        .filter((t) => t.name.toLowerCase().includes(query))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 8),
    );
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }

  startLoading() {
    this.isLoading.set(this.isLoading() + 1);
  }

  stopLoading() {
    this.isLoading.set(Math.max(0, this.isLoading() - 1));
  }

  onSave() {
    if (this.formGroup.valid) {
      this.startLoading();
      const dto: CreateMaintenanceWorkOrderDto = {
        assetId: this.formGroup.value.assetId!,
        siteCode: this.formGroup.value.siteCode,
        maintenanceWorkOrderTypeReference:
          typeof this.formGroup.value.maintenanceWorkOrderTypeReference === 'object'
            ? this.formGroup.value.maintenanceWorkOrderTypeReference.id
            : this.formGroup.value.maintenanceWorkOrderTypeReference!,
        maintenanceWorkOrderReasonReference:
          typeof this.formGroup.value.maintenanceWorkOrderReasonReference === 'object'
            ? this.formGroup.value.maintenanceWorkOrderReasonReference.id
            : this.formGroup.value.maintenanceWorkOrderReasonReference!,
        isMachineDown: this.formGroup.value.isMachineDown!,
        submitterRemarks: this.formGroup.value.submitterRemarks ?? undefined,
        submittedBy: 'tester',
        status: this.formGroup.value.status ?? 0,
      };

      if (!this.report || !this.report.id) {
        this.maintenanceWorkOrderProxyService
          .create(dto)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            if (response.data) {
              GlobalMessageUtil.success('Success', `Maintenance Work Order has been created successfully`);
              this.close({ action: 'save', data: response.data });
            }
          });
      } else {
        this.maintenanceWorkOrderProxyService
          .update(this.report.id, dto)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            GlobalMessageUtil.success('Success', `Maintenance Work Order has been updated successfully`);
            this.close({ action: 'save', data: response.data });
          });
      }
    }
  }
}
